import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import './PdfInlineViewer.css';

// Same self-hosted worker as PdfPreviewModal (audit 2026-05-17: pdf.js used to
// come from unpkg.com, a third-party CDN — supply-chain and GDPR risk). The
// file in frontend/public/pdf/ ships with the pinned pdfjs-dist@3.11.174 and is
// refreshed whenever that package is bumped.
const PDF_WORKER_URL = '/pdf/pdf.worker.min.js';

/**
 * A PDF rendered INSIDE its container, not over the top of the page.
 *
 * Marcel asked to read a bunker note against a delivery's own tonnage
 * "side by side". The split pane put the paperwork beside the delivery, and
 * then opening a document threw a centred pop-up over both of them — so the
 * one thing he asked for was still impossible. The pane was right; the viewer
 * inside it was still the old modal.
 *
 * This is the same @react-pdf-viewer engine as ``PdfPreviewModal`` with the
 * overlay taken off, so a document fills whatever column it is given and the
 * record stays readable next to it. The modal is deliberately still there and
 * still used: on a screen with no pane (the Fuel Enquiry form hosts the same
 * contracts panel) a pop-up is the right answer, and swapping it for an inline
 * viewer there would push the enquiry's own fields off the screen.
 *
 * Download stays available because "read it here" and "keep a copy" are
 * different jobs — the whole point of the change is that reading no longer
 * COSTS you a download and a trip out to Finder.
 */
const PdfInlineViewer = ({ pdfUrl, fileName, onClose, onDownload }) => {
    const handleDownload = useCallback(() => {
        if (onDownload) {
            onDownload();
            return;
        }
        if (!pdfUrl) return;
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = fileName || 'document.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [pdfUrl, fileName, onDownload]);

    if (!pdfUrl) return null;

    return (
        <section
            className="pdf-inline-viewer"
            data-testid="pdf-viewer"
            aria-label={fileName || 'Document'}
        >
            <header className="pdf-inline-viewer-bar">
                <button
                    type="button"
                    className="pdf-inline-viewer-back"
                    onClick={onClose}
                >
                    &#8592; Back to list
                </button>
                <span className="pdf-inline-viewer-name" title={fileName}>
                    {fileName || 'Document'}
                </span>
                <button
                    type="button"
                    className="pdf-inline-viewer-download"
                    onClick={handleDownload}
                >
                    Download
                </button>
            </header>
            <div className="pdf-inline-viewer-canvas">
                <Worker workerUrl={PDF_WORKER_URL}>
                    <Viewer
                        fileUrl={pdfUrl}
                        // The library's own message is "Invalid PDF
                        // structure." — true, and useless to a trader, who
                        // cannot tell from it whether the file is broken,
                        // whether HMS is broken, or what to do next. It also
                        // reads as a system fault when the usual cause is a
                        // file that was never a real PDF.
                        renderError={() => (
                            <div className="pdf-inline-viewer-error">
                                <p className="pdf-inline-viewer-error-title">
                                    This file cannot be displayed
                                </p>
                                <p>
                                    It is not a readable PDF. Download it to
                                    check what was actually uploaded, and
                                    re-upload it if it is wrong.
                                </p>
                            </div>
                        )}
                    />
                </Worker>
            </div>
        </section>
    );
};

PdfInlineViewer.propTypes = {
    pdfUrl: PropTypes.string,
    fileName: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onDownload: PropTypes.func,
};

PdfInlineViewer.defaultProps = {
    pdfUrl: null,
    fileName: '',
    onDownload: null,
};

export default PdfInlineViewer;
